import { defineStore } from 'pinia';
import { computed, ref, shallowRef } from 'vue';
import type { RealtimeEnvelopeType } from '@/contracts/realtime-envelope-v1';
import { DEDUPE_WINDOW_SIZE } from '@/contracts/realtime-policy';

/**
 * Connection state of the realtime socket, as the UI sees it.
 *
 * `idle` — no session / not started. `disabled` — bootstrap said the tenant
 * has realtime off (no socket is ever opened). The rest are pusher-js
 * connection states verbatim: `connecting` (initial or after a drop),
 * `connected`, `unavailable` (pusher-js is retrying), `failed` (no usable
 * transport — terminal until the next start), `disconnected` (we closed it).
 */
export type RealtimeStatus =
  | 'idle'
  | 'connecting'
  | 'connected'
  | 'unavailable'
  | 'failed'
  | 'disconnected'
  | 'disabled';

/**
 * Whether the local view of the stream is known to be complete.
 *
 * `idle` — nothing synced yet (or realtime is off). `syncing` — a
 * `notifications/changes` drain is running. `synchronized` — the last drain
 * reached `has_more: false`; nothing was missed. `degraded` — the drain gave
 * up (network / 5xx after its retries, or the module answered 404): the
 * socket may still deliver, but a gap is possible until the next drain.
 */
export type RealtimeSyncState = 'idle' | 'syncing' | 'synchronized' | 'degraded';

/**
 * What an open page needs to know to repaint itself after an event: which
 * family arrived, and — for messages — which thread. Content-free: never a
 * title, body or channel name. `seq` only ever increases, so a `watch` on the
 * tick fires once per coalesced burst.
 */
export interface RealtimeTick {
  seq: number;
  type: RealtimeEnvelopeType;
  /** The `resource {type,id}` of the LAST envelope in the burst, if any. */
  resource: { type: string; id: string } | null;
  /** The thread of the last message envelope in the burst, if any. */
  thread: { id: string; type: 'direct' | 'group' } | null;
  /** True when the burst came from a catch-up drain rather than a live frame. */
  drained: boolean;
}

/**
 * Content-free frame accounting (phase6-contract.md §3.1 "Store additions").
 * Numbers only — how many envelopes were accepted, deduplicated, rejected by
 * the parser / session binding, refused by a reducer as stale, how many REST
 * reconciles ran, how many catch-up drains completed and how many drained
 * items were rejected. Never a payload, an id or a channel name.
 */
export interface RealtimeCounters {
  accepted: number;
  duplicates: number;
  rejected: number;
  stale: number;
  reconciles: number;
  drains: number;
  drainRejections: number;
}

export type RealtimeCounter = keyof RealtimeCounters;

function freshCounters(): RealtimeCounters {
  return { accepted: 0, duplicates: 0, rejected: 0, stale: 0, reconciles: 0, drains: 0, drainRejections: 0 };
}

/**
 * Realtime connection diagnostics shared between the service and the UI
 * (settings page status row, future header indicator).
 *
 * Content-free by design: it holds connection *state*, never an envelope, a
 * channel name or an error payload. `lastError` is a short reason class —
 * `bootstrap_failed` (the bootstrap request failed/aborted), `origin_mismatch`
 * (dev only: the page is not open at the API origin, which the gateway's Origin
 * allowlist refuses), `connection_error`, `channel_auth_failed`,
 * `subscription_error`, `version_mismatch`, `connection_refused` (a 4001/4007/
 * 4009 close — re-bootstrapping), and the two DEC-102 gateway guards:
 * `gateway_loopback` (HARD, status `failed` — the bootstrap named a loopback
 * gateway while the API is remote, so no socket is opened until a corrected
 * bootstrap; REST polling covers the inbox) and `gateway_unexpected` (ADVISORY
 * — the bootstrap's gateway is not in `VITE_REALTIME_GATEWAY_ORIGINS`; the
 * socket is still dialled and a successful connect clears it) — so a screen
 * can say "something is wrong" (see `realtimeErrorCopy`) without ever echoing
 * server text.
 *
 * Phase 6 adds the stream position (`appliedSequence` — the highest decimal
 * `sequence` accepted, or asserted by a snapshot / a changes page), the dedupe
 * window size, the {@link RealtimeCounters} and `lastRejectReason` (the
 * parser's RULE NAME for the last rejected frame, e.g. `type unknown` — never
 * payload text). `RealtimeStatus` / `RealtimeSyncState` are unchanged.
 */
export const useRealtimeStore = defineStore('realtime', () => {
  const status = ref<RealtimeStatus>('idle');
  const socketId = ref<string | null>(null);
  /** Epoch ms of the last accepted envelope, or `null`. */
  const lastEventAt = ref<number | null>(null);
  /** Short, content-free reason class of the last failure, or `null`. */
  const lastError = ref<string | null>(null);
  /** The server's bootstrap verdict; `false` means no socket is ever opened. */
  const enabled = ref(false);
  /** Whether the catch-up stream is known to be complete (see {@link RealtimeSyncState}). */
  const syncState = ref<RealtimeSyncState>('idle');
  /** True once a durable cursor is held (persisted per tenant + user). */
  const hasCursor = ref(false);
  /** The last coalesced burst, for open pages to repaint on (see {@link RealtimeTick}). */
  const lastTick = shallowRef<RealtimeTick | null>(null);
  /** The highest stream sequence applied (decimal string), or `null` before anything was. */
  const appliedSequence = ref<string | null>(null);
  /** Capacity of the `event_id` dedupe window the service runs. */
  const dedupeSize = ref(DEDUPE_WINDOW_SIZE);
  /** Content-free frame accounting (see {@link RealtimeCounters}). */
  const counters = ref<RealtimeCounters>(freshCounters());
  /** The parser's rule name for the last rejected frame, or `null`. */
  const lastRejectReason = ref<string | null>(null);

  const isConnected = computed(() => status.value === 'connected');
  /** True while pusher-js is between attempts (the "Reconnecting…" UI state). */
  const isReconnecting = computed(
    () => status.value === 'unavailable' || (status.value === 'connecting' && lastEventAt.value !== null),
  );

  let tickSeq = 0;

  function setStatus(next: RealtimeStatus): void {
    status.value = next;
    if (next !== 'connected') socketId.value = null;
  }

  function setSocketId(id: string | null): void {
    socketId.value = id;
  }

  function markEvent(): void {
    lastEventAt.value = Date.now();
  }

  function setError(reason: string | null): void {
    lastError.value = reason;
  }

  function setEnabled(value: boolean): void {
    enabled.value = value;
  }

  function setSyncState(next: RealtimeSyncState): void {
    syncState.value = next;
  }

  function setHasCursor(value: boolean): void {
    hasCursor.value = value;
  }

  /** Publish a repaint tick. Pages `watch(() => realtimeStore.lastTick, …)`. */
  function tick(payload: Omit<RealtimeTick, 'seq'>): void {
    tickSeq += 1;
    lastTick.value = { seq: tickSeq, ...payload };
  }

  /** The service's applied stream position (already compared by the service). */
  function setAppliedSequence(sequence: string | null): void {
    appliedSequence.value = sequence;
  }

  function setDedupeSize(size: number): void {
    dedupeSize.value = size;
  }

  /** Increment one counter by `by` (default 1). */
  function bump(counter: RealtimeCounter, by = 1): void {
    counters.value[counter] += by;
  }

  /** The parser's rule name for a rejected frame (content-free), or `null`. */
  function setLastRejectReason(reason: string | null): void {
    lastRejectReason.value = reason;
  }

  /** Clear everything — on sign-out and whenever the service stops. */
  function reset(): void {
    status.value = 'idle';
    socketId.value = null;
    lastEventAt.value = null;
    lastError.value = null;
    enabled.value = false;
    syncState.value = 'idle';
    hasCursor.value = false;
    lastTick.value = null;
    appliedSequence.value = null;
    dedupeSize.value = DEDUPE_WINDOW_SIZE;
    counters.value = freshCounters();
    lastRejectReason.value = null;
  }

  return {
    status,
    socketId,
    lastEventAt,
    lastError,
    enabled,
    syncState,
    hasCursor,
    lastTick,
    appliedSequence,
    dedupeSize,
    counters,
    lastRejectReason,
    isConnected,
    isReconnecting,
    setStatus,
    setSocketId,
    markEvent,
    setError,
    setEnabled,
    setSyncState,
    setHasCursor,
    tick,
    setAppliedSequence,
    setDedupeSize,
    bump,
    setLastRejectReason,
    reset,
  };
});
